import { useState, useMemo, useRef, useEffect } from "react";
import { Avatar, Menu, ActionIcon, Stack, Flex, Text, Anchor, Tooltip, Box, Textarea } from "@mantine/core";
import {
  IconTrash,
  IconPencil,
  IconCircleCheck,
  IconPlus
} from "@tabler/icons";
import { AssignNewideaIcon, CloseIcon, DeliveryHeaderIcon, OwnerHeaderIcon, ProductAreaHeaderIcon } from '../icons/x-symbol-svgrepo-com';
import { truncateTitle, formatDate } from "../context/helpers";
import { useAuth0 } from "@auth0/auth0-react";
export default function ActivityFeed({
  comments,
  deleteComment,
  editComment,
  activityLogs,
  commentId,
  parent  
}) {
  const [selectedTab, setSelectedTab] = useState(commentId ? "messages" : "all");
  const [commentValue, setCommentValue] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingValue, setEditingValue] = useState("");
  const auth0 = useAuth0();
  const loggedInUserMail = auth0.user?.email;
  // Function to handle edited comment submission
  const handleEditSubmit = async (comment, value) => {
    if (!value.trim()) return;
    setCommentValue(value.trim());
    await editComment(comment, value.trim()); // Add the comment using the hook's function
    setCommentValue("");
    setEditingValue("");
    setEditingId(null);
  };

  const getFieldIcon = (type) => {
    switch (type.toLowerCase()) {
    case 'assigned':
      return <IconCircleCheck size={16} fill="white" color="grey" />; 
  
    case 'unlinked':
      return <DeliveryHeaderIcon/>;
  
    case 'validated':
      return <AssignNewideaIcon/>; 

    case 'created':
      return <IconPlus size={16} fill="white" color="grey" />;

    case 'dismissed':
      return <CloseIcon/>;
    
    case 'owner':
      return <OwnerHeaderIcon/>;

    case 'product_area':
      return <ProductAreaHeaderIcon/>;
    }
  };

  const ItemComponent = ({ field, value }) => (
    <Flex>
      {/* <Flex
        w="24px"
        h="24px"
        m={5}
        bg="#E9ECEF"
        sx={{ borderRadius: 2, padding: 5, backgroundColor: 'transparent', lineHeight: 16}}
      >
        {getFieldIcon(field)}  
      </Flex> */}
      <Flex
        w="100%"
        h="100%"
        m={3}
        bg="#E9ECEF"
        sx={{ borderRadius: 2, padding: 3, backgroundColor: 'transparent', lineHeight: 16}}
      >
        <Text 
          size="sm" 
          style={{
            fontFamily: 'Inter',
            fontSize: 12,
            marginBottom: 2,
            letterSpacing: 0,
            verticalAlign: 'middle',
          }}
        >
          {value}
        </Text>
      </Flex>
    </Flex>
  );


  function getActivityMessage(log) {
    switch (log.logType) {
    case "idea_reassigned":
      return (
        <Stack spacing={0} ml="-10px" mt={-14} w="100%">
          <ItemComponent
            field='assigned'
            value={
              <>
                Evidence Reassigned to idea{" "}
                <Tooltip label={`${log.ideaToTitle}`}>
                  <Anchor href={`/idea/${log.ideaIdTo}`} target="_blank" sx={{ color: '#5C5CEB' }}>
                    <b>"{truncateTitle(log.ideaToTitle)}"</b>
                  </Anchor>
                </Tooltip>
                {" "}from{" "}
                <Tooltip label={`${log.ideaFromTitle}`}>
                  <Anchor href={`/idea/${log.ideaIdFrom}`} target="_blank" sx={{ color: '#5C5CEB' }}>
                    <b>"{truncateTitle(log.ideaFromTitle)}"</b>
                  </Anchor>
                </Tooltip>
              </>
            }
          />
        </Stack>
      );
    case "idea_assigned":
      return (
        <Stack spacing={0} ml="-10px" p={4} w="100%">
          <ItemComponent
            field='assigned'
            value={
              <>
                Evidence Assigned to idea{" "}
                <Tooltip label={`${log.ideaToTitle}`}>
                  <Anchor href={`/idea/${log.ideaIdTo}`} target="_blank" sx={{ color: '#5C5CEB' }}>
                    <b>"{truncateTitle(log.ideaToTitle)}"</b>
                  </Anchor>
                </Tooltip>
              </>
            }
          />
        </Stack>
      );
    case "idea_unlinked":
      return (
        <Stack spacing={0} ml="-10px" p={4} w="100%">
          <ItemComponent
            field='unlinked'
            value={
              <>
                Evidence Unlinked from idea{" "}
                <Tooltip label={`${log.ideaFromTitle}`}>
                  <Anchor href={`/idea/${log.ideaIdFrom}`} target="_blank" sx={{ color: '#5C5CEB' }}>
                    <b>"{truncateTitle(log.ideaFromTitle)}"</b>
                  </Anchor>
                </Tooltip>
              </>
            }
          />
        </Stack>
      );
    case "evidence_validated":
      return <span>Evidence validated</span>;
    case "evidence_updated":
      if (log.priorityFrom !== undefined || log.priorityTo !== undefined) {
        return (
          <Stack spacing={0} ml="-10px" p={4} w="100%">
            <ItemComponent
              field='priority'
              value={
                <>
                  Priority was changed from <b>"{log.priorityFrom || 'None'}"</b> to <b>"{log.priorityTo || 'None'}"</b>
                </>
              }
            />
          </Stack>
        );
      } else if (log.ownerNameFrom !== undefined || log.ownerNameTo !== undefined) { 
        return (
          <Stack spacing={0} ml="-10px" p={4} w="100%">
            <ItemComponent
              field='owner'
              value={
                <>
                  Owner was changed from <b>"{log.ownerNameFrom || 'None'}"</b> to <b>"{log.ownerNameTo || 'None'}"</b>
                </>
              }
            />
          </Stack>
        );
      } else if (log.stateFrom !== undefined || log.stateTo !== undefined) {
        return (
          <Stack spacing={0} ml="-10px" p={4} w="100%">
            <ItemComponent
              field='state'
              value={
                <>
                  State was changed from <b>"{log.stateFrom || 'None'}"</b> to <b>"{log.stateTo || 'None'}"</b>
                </>
              }
            />
          </Stack>
        );
      } else if (log.productAreaFromName !== undefined || log.productAreaToName !== undefined) {
        return (
          <Stack spacing={0} ml="-10px" p={4} w="100%">
            <ItemComponent
              field='product_area'
              value={
                <>
                  Product area was changed from <b>"{log.productAreaFromName || 'None'}"</b> to <b>"{log.productAreaToName || 'None'}"</b>
                </>
              }
            />
          </Stack>
        );
      } else { 
        return <span>Evidence updated</span>;
      }
    case "owner_updated":
      return (
        <Stack spacing={0} ml="-10px" p={4} w="100%">
          <ItemComponent
            field='owner'
            value={
              <>
                Owner was changed from <b>"{log.ownerNameFrom || 'None'}"</b> to <b>"{log.ownerNameTo || 'None'}"</b>
              </>
            }
          />
        </Stack>
      )
    case "product_area_updated":
      return (
        <Stack spacing={0} ml="-10px" p={4} w="100%">
          <ItemComponent
            field='product_area'
            value={
              <>
                Product area was changed from <b>"{log.productAreaFromName || 'None'}"</b> to <b>"{log.productAreaToName || 'None'}"</b>
              </>
            }
          />
        </Stack>
      );
    case "evidence_created":
      return (
        <Stack spacing={0} ml="-10px" p={4} w="100%">
          <ItemComponent
            field='created'
            value={
              <>
                Evidence Created by{" "}
                <Tooltip label={`${log.itemLink}`}>
                  <Anchor href={log.itemLink} target="_blank" sx={{ color: '#5C5CEB' }}>
                    <b>{log.origin}</b>
                  </Anchor>
                </Tooltip>
                {" "}<b>"{log.itemType}"</b>
              </>
            }
          />
        </Stack>
      );
    case "evidence_submitted":
      return (
        <Stack spacing={0} ml="-10px" p={4} w="100%">
          <ItemComponent
            field='created'
            value={
              <>
                Evidence <b>Submitted</b>
              </>
            }
          />
        </Stack>
      );
    case "synced_to_external":
      const syncedFields = log.syncedFields || log.context?.syncedFields || [];
      const hasFields = syncedFields.length > 0;
      const action = log.action || log.context?.action;
      const integration = log.integration || log.context?.integration || 'external system';
      
      return (
        <Stack spacing={0} ml="-10px" p={4} w="100%">
          <ItemComponent
            field='synced'
            value={
              <>
                Evidence <b>Synced</b> to{' '}
                <b>{integration}</b>
                {action && ` (${action})`}
                {hasFields && (
                  <>
                    <br />
                    <span style={{ fontSize: '11px', color: '#868E96', fontStyle: 'italic' }}>
                      Fields: {syncedFields.join(', ')}
                    </span>
                  </>
                )}
              </>
            }
          />
        </Stack>
      );
    // Add more cases as needed
    default:
      return <span>{log.eventType}</span>;
    }
  }

  // Prepare merged and sorted feed
  const mergedFeed = useMemo(() => {
    const commentItems = comments.map((c) => ({
      id: c._id,
      type: "comment",
      user: { name: c.userName, avatar: c.userName?.[0] || "U", email: c.userEmail },
      date: c.createdAt,
      content: <span dangerouslySetInnerHTML={{ __html: c.text }} />,
      raw: c, 
      status: c.status
    }));

    let activityItems  = []
    if(activityLogs?.activityLogs?.length > 0) {
      activityItems = activityLogs.activityLogs.flatMap((log, idx) => {
        if (log.logType === "idea_reassigned") {
          const items = [];
          items.push({
            id: (log._id || log.timestamp || idx) + "-unassigned",
            type: "activity",
            user: { name: log.userName, avatar: (log.userName?.[0]) },
            date: log.timestamp,
            content: (
              <Stack spacing={0} ml="-10px" p={4} w="100%">
                <ItemComponent
                  field='assigned'
                  value={
                    <>
                      Evidence Reassigned to idea{" "}
                      <Tooltip label={`${log.ideaToTitle}`}>
                        <Anchor href={`/idea/${log.ideaIdTo}`} target="_blank" sx={{ color: '#5C5CEB' }}>
                          <b>"{truncateTitle(log.ideaToTitle)}"</b>
                        </Anchor>
                      </Tooltip>
                      {" "}from{" "}
                      <Tooltip label={`${log.ideaFromTitle}`}>
                        <Anchor href={`/idea/${log.ideaIdFrom}`} target="_blank" sx={{ color: '#5C5CEB' }}>
                          <b>"{truncateTitle(log.ideaFromTitle)}"</b>
                        </Anchor>
                      </Tooltip>
                    </>
                  }
                />
              </Stack>
            ),
          });
        
          items.push({
            id: (log._id || log.timestamp || idx) + "-assigned",
            type: "activity",
            user: { name: log.userName || "Unknown", avatar: (log.userName?.[0] || "U") },
            date: log.timestamp,
            content: (
              <Stack spacing={0} ml="-10px" p={4} w="100%">
                <ItemComponent
                  field='assigned'
                  value={
                    <>
                      <b>Source of Assignment</b> - <b>"{log.sourceOfAssignment}"</b>
                    </>
                  }
                />
              </Stack>
            ),
          });
          
          return items;
        }
        return {
          id: log._id || log.timestamp || idx,
          type: "activity",
          user: { name: log.userName, avatar: (log.userName?.[0]) },
          date: log.timestamp,
          content: getActivityMessage(log),
        };
      });
    }
    // Merge and sort by date descending
    return [...commentItems, ...activityItems].sort(
      (a, b) => new Date(b.date) - new Date(a.date)
    );
  }, [comments, activityLogs]);
  // Filtered feed for the selected tab
  const feedToShow =
    selectedTab === "all"
      ? mergedFeed
      : mergedFeed.filter((item) => item.type === "comment");

  const UserAvatar = ({ initial }) => (
    <Avatar
      radius="xl"
      size={16}
      styles={(theme) => ({ 
        root: {
          backgroundColor: '#585C68',
          border: 'none',
          color: 'white',
        },
        placeholder: {
          color: 'white',
          fontSize: '10px',
          backgroundColor: '#585C68',
          fontWeight: 500
        }
      })}
    >
      {initial}
    </Avatar>
  );
      
  const commentRef = useRef(null);
  // Add useEffect to handle scrolling and highlighting when redirect from notification
  useEffect(() => {
    const scrollToComment = () => {
      if (commentId && commentRef.current) {
        // Get the feed container
        const feedContainer = document.querySelector('div[style*="height: 450px"]');
        if (feedContainer) {
          const commentPosition = commentRef.current.offsetTop;
          const containerHeight = feedContainer.clientHeight;
          const scrollTo = commentPosition - (containerHeight / 2) + (commentRef.current.clientHeight / 2);
          
          // Smooth scroll within the container
          feedContainer.scrollTo({
            top: scrollTo,
            behavior: 'smooth'
          });
          const element = commentRef.current;
          element.style.backgroundColor = '#F0F0FF';
          const timer = setTimeout(() => {
            element.style.backgroundColor = 'transparent';
          }, 3000);
          return () => clearTimeout(timer);
        }
      }
    };
    const timer = setTimeout(scrollToComment, 100);
    return () => clearTimeout(timer);
  }, [commentId, feedToShow]);

  // Calculate unread notification count
  const unreadNotificationCount = useMemo(() => {
    return feedToShow.filter(item => item.status === 'unread').length;
  }, [feedToShow]);


  return (
    <div style={{ maxWidth: parent === "inbox" ? '100%' : 500, width: parent === "inbox" ? '100%' : '426px', paddingTop : parent === "inbox" ? '' : '20px' , margin: parent === "inbox" ? 0 : "0 auto", fontFamily: "Inter, sans-serif", boxSizing: 'border-box' }}>
      {/* Tabs */}
      <div style={{ display: "flex", borderRadius: 0, background: "#5C5CEB1A", marginBottom: 24 }}>
        <button
          onClick={() => setSelectedTab("messages")}
          style={{
            flex: 1,
            padding: "12px 0",
            border: "none",
            margin: 5,
            height: 36,
            background: selectedTab === "messages" ? "#fff" : "transparent",
            color: "#161832",
            fontWeight: 600,
            borderRadius: 0,
            fontSize: 14,
            cursor: "pointer",
            boxShadow: selectedTab === "messages" ? "0 2px 8px #eaeafe" : "none",
            transition: "background 0.2s",
          }}
        >
          {unreadNotificationCount > 0 && (
            <span style={{
              display: "inline-block",
              width: 8,
              height: 8,
              background: "#2684FF",
              borderRadius: "50%",
              marginRight: 8,
              verticalAlign: "middle"
            }} />
          )}
          Comments
        </button>
        <button
          onClick={() => setSelectedTab("all")}
          style={{
            flex: 1,
            padding: "12px 5px",
            border: "none",
            margin: 5,
            height: 36,
            gap: 8,
            background: selectedTab === "all" ? "#fff" : "transparent",
            color: "#161832",
            fontWeight: 600,
            borderRadius: 4,
            fontSize: 14,
            cursor: "pointer",
            boxShadow: selectedTab === "all" ? "0 2px 8px #eaeafe" : "none",
            transition: "background 0.2s",
          }}
        >
          All activities
        </button>
      </div>

      {/* Feed List */}
      <div style={{ height: 450, width: parent === "inbox" ? '100%' : '426px', overflowY: "auto", boxSizing: 'border-box' }}>
        {feedToShow.map((item) => (
          <div 
            key={item.id} 
            ref={item.id === commentId ? commentRef : null}
            style={{ 
              width: parent === "inbox" ? '100%' : '426px',
              marginBottom: 28,
              transition: 'background-color 0.3s ease',
              padding: item.type === 'comment' ? '12px' : '4px 0px 0px 12px',
              backgroundColor:
                item.id === commentId
                  ? '#F0F0FF'
                  : item.type === 'comment'
                    ? '#F8F9FA'
                    : 'transparent',
              border: item.type === 'comment' ? '1px solid #D8D8DB' : 'none'
            }}
          >
            <div style={{ display: "flex", alignItems: "center", marginBottom: -4, position: 'relative' }}>
              {item.user.name && (
                <>
                  {item.status === 'unread' && (
                    <div style={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      backgroundColor: '#2684FF',
                      marginRight: 8,
                      marginLeft: 2
                    }} />
                  )}
                  {/* Avatar */}
                  <UserAvatar initial={item.user.avatar} />
                  {/* Name and date */}
                  <span style={{ fontWeight: 600, color: "#212529", marginRight: 8, marginLeft: 8, fontSize: 12 }}>
                    {item.user.name}
                  </span>
                </>

              )}

              <span style={{ color: "#585C68", fontSize: 12 }}>
              • {formatDate(item.date)}
              </span>
              {item.type === 'comment' && item.user.email === loggedInUserMail && (
                <div style={{ position: 'absolute', right: 0, top: 0 }}>
                  <Menu withinPortal id= 'menu-item' position="bottom-end" shadow="md">
                    <Menu.Target>
                      <ActionIcon variant="subtle" color="gray" size={24}>
                        ...
                      </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown id="comments-menu-dropdown">
                      <Menu.Item
                        icon={<IconPencil size={14} />}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setEditingId(item.id);
                          setEditingValue(item.raw.text.replace(/<[^>]+>/g, ""));
                        }}
                      >
                        Edit
                      </Menu.Item>
                      <Menu.Item
                        color="red"
                        icon={<IconTrash size={14} />}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          deleteComment(item.id)
                        }}
                      >
                        Delete
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                </div>
              )}
            </div>
            <div
              style={{
                background: "transparent",
                borderRadius: 0,
                marginLeft: 0,
                color: "#212529",
                fontSize: 12,
                fontWeight: 400,
                marginBottom: -15,
                lineHeight: 2,
                marginTop: 5
              }}
            >
              {editingId === item.id ? (
                <Box style={{ marginBottom: '16px', marginTop: '16px' }}> {/* Consistent container */}
                  <Textarea 
                    value={editingValue}
                    onChange={(e) => setEditingValue(e.target.value)}
                    styles={{
                      input: {
                        marginBottom: 0, // Remove default margin
                        padding: '12px',
                        minHeight: 'auto'
                      }
                    }}
                  />
                  <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
                    <button
                      onClick={async () => {
                        await handleEditSubmit(item.raw, editingValue);
                      }}
                      style={{ padding: "6px 16px", background: "#5B60E3", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer" }}
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditingId(null)}
                      style={{ padding: "6px 16px", background: "#eee", color: "#333", border: "none", borderRadius: 4, cursor: "pointer" }}
                    >
                      Cancel
                    </button>
                  </div>
                </Box>
              ) : (
                <Text style={{ marginBottom: 16, marginTop: 16 }}> {/* Remove default margin */}
                  {item.content}
                </Text>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
} 